import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileContainer, mutationErrorHandler } from "am-tax-fe-core";

import { TOAST_DURATION } from "../../utils/constants";

import {
    getTransactionAsync,
    getTransactionsAsync,
    updateTransactionAsync,
    deleteTransactionAsync,
    UpdateTransaction,
    CreateTransaction,
    getTransactionsByClientIdAsync,
    uploadBenchmarkFileAsync,
    createTransactionWithBenchmarkAnalysisAsync,
    getTransactionsForUserAsync,
} from "./transactionApi";
import { queryKeys } from "../queryKeys";

export function useGetTransactions() {
    return useQuery({ ...queryKeys.transactions.all, queryFn: getTransactionsAsync });
}

export function useGetTransactionsForUser() {
    return useQuery({ ...queryKeys.transactions.users, queryFn: getTransactionsForUserAsync });
}

export function useGetTransactionsByClientId(clientId: string | undefined) {
    const toast = useToast();
    return useQuery({
        ...queryKeys.transactions.byClientId(clientId || ""),
        queryFn: async () => {
            const transactions = await getTransactionsByClientIdAsync(clientId || "")
                .then(transactions => {
                    return transactions;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the transactions information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return transactions;
        },
        enabled: !!clientId,
    });
}

export const useGetTransaction = (id: string | undefined) => {
    const toast = useToast();
    return useQuery({
        ...queryKeys.transactions.byId(id || ""),
        queryFn: async () => {
            const transaction = await getTransactionAsync(id || "")
                .then(transaction => {
                    return transaction;
                })
                .then(res => res)
                .catch(err => {
                    toast({
                        title: "Something went wrong",
                        description: "Error loading the transaction information.",
                        status: "error",
                        isClosable: true,
                    });
                    return err;
                });
            return transaction;
        },
        enabled: !!id,
    });
};

export function useUpdateTransaction() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer | undefined; transaction: UpdateTransaction }) => {
            return await updateTransactionAsync(args.transaction, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.transactions.all);
            toast({
                title: "Updated",
                description: "Transaction updated successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to update transaction. Make sure you are using the benchmark template provided in the portal."),
    });
}

export function useDeleteTransaction() {
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: string) => {
            return await deleteTransactionAsync(args);
        },
        onSuccess: async () => {
            toast({
                title: "Deleted",
                description: "Transaction deleted successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to delete transaction"),
    });
}

export function useUploadBenchmarkDiagram() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer; transactionId: string }) => {
            return await uploadBenchmarkFileAsync(args.file, args.transactionId);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.transactions.all);
            toast({
                title: "Uploaded",
                description: "Benchmark file uploaded successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to upload benchmark file. Make sure you are using the benchmark template provided in the portal."),
    });
}

export function useCreateTransactionWithBenchmarkAnalysisAsync() {
    const queryClient = useQueryClient();
    const toast = useToast();
    return useMutation({
        mutationFn: async (args: { file: FileContainer; transaction: CreateTransaction }) => {
            return await createTransactionWithBenchmarkAnalysisAsync(args.transaction, args.file);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(queryKeys.transactions.all);
            toast({
                title: "Uploaded",
                description: "Benchmark file uploaded successfully.",
                status: "success",
                duration: TOAST_DURATION,
                isClosable: true,
            });
        },
        ...mutationErrorHandler(toast, "Unable to upload benchmark file. Make sure you are using the benchmark template provided in the portal."),
    });
}
